import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HomeIcon from '@mui/icons-material/Home';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material';
import type { Navigation } from '@toolpad/core';
import { Account, AppProvider, DashboardLayout as ToolpadDashboardLayout } from '@toolpad/core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import ProcurementStep1Welcome from './components/ProcurementStep1Welcome';
import ProcurementStep2HowItWorks from './components/ProcurementStep2HowItWorks';
import ProcurementStep3Complete from './components/ProcurementStep3Complete';
import Step1Welcome from './components/Step1Welcome';
import Step2CompanyInfo from './components/Step2CompanyInfo';
import Step3DocLibrary from './components/Step3DocLibrary';
import Step4NewTender from './components/Step4NewTender';
import Step5Complete from './components/Step5Complete';

const BIDDING_STEPS = ['Welcome', 'Company Info', 'Document Library', 'New Tender', 'Complete'];
const PROCUREMENT_STEPS = ['Welcome to Procurement', 'How it Works', 'Complete'];

const MOCK_NAVIGATION: Navigation = [
  { segment: 'dashboard', title: 'My Tenders', icon: <HomeIcon /> },
  { segment: 'public-tenders', title: 'Public Tenders', icon: <FormatListBulletedIcon /> },
  { segment: 'library', title: 'Knowledge Base', icon: <LibraryBooksIcon /> },
  { segment: 'templates', title: 'Templates', icon: <DescriptionIcon /> },
  { segment: 'company-info', title: 'Company Info', icon: <BusinessIcon /> },
];

const MOCK_PROCUREMENT_NAVIGATION: Navigation = [
  { segment: 'procure/dashboard', title: 'My Procurements', icon: <HomeIcon /> },
  // { segment: 'procure/archive', title: 'Archive', icon: <ArchiveIcon /> },
];

const ToolbarActions = () => {
  const theme = useTheme();
  const { isLoggingOut } = useAuth();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        sx={{
          backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
          color: 'white',
          fontWeight: 'bold',
          mr: 1,
          display: { xs: 'none', sm: 'flex' }, // Hide on very small screens if needed, or keep always visible
        }}
      >
        New Tender
      </Button>
      {isLoggingOut ? <CircularProgress size={24} color="inherit" /> : <Account />}
    </Box>
  );
};

export default function OnboardingWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const [step4View, setStep4View] = useState<'menu' | 'public' | 'manual'>('menu');
  const [createdTenderId, setCreatedTenderId] = useState<string | null>(null);
  const { account, refreshUserDetails, setIsFirstLogin } = useAccount();
  const navigate = useNavigate();
  const theme = useTheme();

  const isProcurementOnly = account?.clientSettings?.enableBiddingModule === false;
  const steps = isProcurementOnly ? PROCUREMENT_STEPS : BIDDING_STEPS;

  // Load saved progress
  useEffect(() => {
    if (account?.preferences?.onboardingStep) {
      setActiveStep(Number(account.preferences.onboardingStep));
    }
  }, [account?.preferences?.onboardingStep]);

  const handleNext = async (tenderId?: string) => {
    if (tenderId && typeof tenderId === 'string') {
      setCreatedTenderId(tenderId);
    }

    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    setStep4View('menu'); // Reset view on step change

    // Save progress
    try {
      await apiService.updateAccountDetails({
        preferences: {
          ...account?.preferences,
          onboardingStep: nextStep,
        },
      });
      await refreshUserDetails();
    } catch (_e) {
      // Ignore error as this is just saving progress
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setStep4View('menu'); // Reset view on step change
  };

  const handleComplete = async () => {
    try {
      await apiService.updateAccountDetails({
        preferences: {
          ...account?.preferences,
          onboardingStep: steps.length,
          onboardingCompleted: true,
        },
      });
      setIsFirstLogin(false);
      await refreshUserDetails();

      if (createdTenderId) {
        navigate(`/tenders/${createdTenderId}/go-no-go`);
      } else if (isProcurementOnly) {
        navigate('/procure/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (_e) {
      if (isProcurementOnly) {
        navigate('/procure/dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const mockRouter = useMemo(() => {
    let pathname = '/onboarding';
    if (activeStep === 1) {
      pathname = '/company-info';
    }
    if (activeStep === 2) {
      pathname = '/admin/knowledge_base';
    }
    if (activeStep === 3) {
      if (step4View === 'public') {
        pathname = '/public-tenders';
      }
    }
    if (activeStep === 4) {
      pathname = '/dashboard';
    }

    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: () => {
        // Mock navigate
      },
    };
  }, [activeStep, step4View]);

  const wizardContent = (
    <Container
      maxWidth="lg"
      sx={{ py: 4, height: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', m: 0 }}>
          Welcome to BIDLOGIQ
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': { fontSize: '1.2rem' },
                '& .MuiStepIcon-root': { width: 36, height: 36 },
                '& .MuiStepIcon-text': { fontWeight: 'bold', fontSize: '1rem' },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper
        elevation={3}
        sx={{ flexGrow: 1, p: 4, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {!isProcurementOnly ? (
            <>
              {activeStep === 0 && <Step1Welcome onNext={handleNext} />}
              {activeStep === 1 && <Step2CompanyInfo onNext={handleNext} onBack={handleBack} />}
              {activeStep === 2 && <Step3DocLibrary onNext={handleNext} onBack={handleBack} />}
              {activeStep === 3 && (
                <Step4NewTender
                  onNext={handleNext}
                  onBack={handleBack}
                  onViewChange={setStep4View}
                />
              )}
              {activeStep === 4 && (
                <Step5Complete onComplete={handleComplete} onBack={handleBack} />
              )}
            </>
          ) : (
            <>
              {activeStep === 0 && <ProcurementStep1Welcome onNext={handleNext} />}
              {activeStep === 1 && (
                <ProcurementStep2HowItWorks onNext={handleNext} onBack={handleBack} />
              )}
              {activeStep === 2 && (
                <ProcurementStep3Complete onComplete={handleComplete} onBack={handleBack} />
              )}
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );

  const { logout } = useAuth();

  const session = account
    ? {
        user: {
          name: account.displayName || account.email || null,
          email: account.email || null,
          image: account.photoURL,
        },
      }
    : null;

  const authentication = {
    signIn: () => Promise.resolve({}),
    signOut: async () => {
      try {
        await logout();
        return { success: true };
      } catch (_error) {
        return { success: false };
      }
    },
  };

  return (
    <AppProvider
      navigation={isProcurementOnly ? MOCK_PROCUREMENT_NAVIGATION : MOCK_NAVIGATION}
      router={mockRouter}
      theme={theme}
      session={session}
      authentication={authentication}
      branding={{
        title: '',
        logo: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <img
              src={
                theme.palette.mode === 'dark'
                  ? '/Bidlogiq_Logo-Long_Reversed.png'
                  : '/Bidlogiq_Logo-Long_Full colours.png'
              }
              alt="BIDLOGIQ Logo"
              style={{
                height: 56,
                width: 'auto',
                maxHeight: 60,
                display: 'block',
              }}
            />
          </Box>
        ),
      }}
    >
      <ToolpadDashboardLayout
        sx={{
          '& .MuiDrawer-docked, & .MuiDrawer-paper': {
            width: `${theme.drawerWidth || 280}px !important`,
            minWidth: `${theme.drawerWidth || 280}px !important`,
            maxWidth: `${theme.drawerWidth || 280}px !important`,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            pointerEvents: 'none', // Make the sidebar unclickable
          },
          '& .MuiToolbar-root': {
            padding: `${theme.spacing(0, 3)} !important`,
          },
          '& .MuiContainer-root': {
            backgroundColor: theme.palette.background.default,
            padding: theme.spacing(1, 3),
          },
          '& [class*="PageContent-root"], & .MuiDrawer-docked + *': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          '& [class*="Breadcrumbs-root"]': {
            display: 'none !important',
          },
          '& .MuiListItemButton-root:not(.Mui-selected)': {
            opacity: 0.6, // Fade unselected items
          },
          '& .MuiListItemText-primary': {
            fontSize: '1.3rem',
            fontWeight: 500,
          },
          '& .MuiListItemButton-root.Mui-selected': {
            backgroundColor: `${theme.palette.bidlogiq?.brick || theme.palette.primary.main} !important`,
            color: '#ffffff !important',
            opacity: '1 !important',
            '& .MuiListItemText-primary': {
              color: '#ffffff !important',
              fontWeight: 600,
            },
            '& .MuiListItemIcon-root': {
              color: '#ffffff !important',
            },
            '& svg path': {
              fill: '#ffffff !important',
              stroke: '#ffffff !important',
            },
          },
        }}
        slots={{
          toolbarActions: ToolbarActions,
        }}
        disableCollapsibleSidebar
      >
        {wizardContent}
      </ToolpadDashboardLayout>
    </AppProvider>
  );
}
